import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Header from "./Header";
import Footer from "./Footer";
import { selectUserId, selectToken } from "../redux/slices/authSlice";

function Settings() {
  const userId = useSelector(selectUserId);
  const token = useSelector(selectToken);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    profilePic: "",
    bankAccountNo: "",
    confirmBankAccountNo: "",
    ifsc: "",
    nameOnBank: "",
    upiId: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const BASE_URL =
    "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Profile";

  // ✅ Fetch profile (INLINE API)
  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId || !token) return;

      try {
        setLoading(true);

        const res = await fetch(`${BASE_URL}/GetUserProfile?userId=${userId}`, {
          headers: {
            Authorization: token,
          },
        });

        const data = await res.json();
        const user = data?.data || data;

        setForm({
          name: user?.name || "",
          email: user?.email || "",
          phone: user?.phone || "",
          profilePic: user?.profilePic || "",
          bankAccountNo: user?.bankAccountNo || "",
          confirmBankAccountNo: user?.bankAccountNo || "",
          ifsc: user?.ifsc || "",
          nameOnBank: user?.nameOnBank || "",
          upiId: user?.upiId || "",
        });
      } catch (err) {
        console.error(err);
        setMessage("Failed to load profile ❌");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, token]);

  // ✅ Handle input
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Update profile (INLINE API)
  const handleSubmit = async () => {
    if (!form.name) {
      setMessage("Name is required ⚠️");
      return;
    }

    if (form.bankAccountNo !== form.confirmBankAccountNo) {
      setMessage("Bank account numbers do not match ❌");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const res = await fetch(`${BASE_URL}/UpdateProfile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          userId,
          name: form.name,
          email: form.email,
          phone: form.phone,
          profilePic: form.profilePic,
          bankAccountNo: form.bankAccountNo,
          ifsc: form.ifsc,
          nameOnBank: form.nameOnBank,
          upiId: form.upiId,
        }),
      });

      const text = await res.text();
      console.log("RAW RESPONSE:", text);

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }

      if (res.ok) {
        setMessage(data?.message || "Profile updated successfully ✅");
      } else {
        setMessage(data?.message || "Update failed ❌");
      }
    } catch (err) {
      console.error("ERROR:", err);
      setMessage("Network error ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />

      <section>
        <div className="container mb-4 mt-2">
          <div className="row pt-4">
            <div className="col-12">
              <span className="badge bg-dark-soft rounded-pill fs-5 px-4">
                Account <span className="fw-bold">Settings</span>
              </span>
              <p className="m-0 fs-4 lh-sm mt-1">
                Your Account Settings / Manage Profile
              </p>
            </div>

            <div className="mt-4">
              <div className="row g-2">
                {/* Phone */}
                <div className="col-md-4">
                  <label className="form-label">
                    User ID <span className="fw-bold text-danger">*</span>
                  </label>
                  <input
                    className="form-control bg-light"
                    name="phone"
                    value={userId}
                    readOnly
                  />
                </div>

                {/* Name */}
                <div className="col-md-4">
                  <label className="form-label">
                    Full Name <span className="fw-bold text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>

                {/* Email */}
                <div className="col-md-4">
                  <label className="form-label">
                    Email ID <span className="fw-bold text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                {/* Phone */}
                <div className="col-md-4">
                  <label className="form-label">
                    Mobile No. <span className="fw-bold text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">+91</span>
                    <input
                      type="tel"
                      className="form-control bg-light"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      readOnly
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Bank Account No.{" "}
                    <span className="fw-bold text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="bankAccountNo"
                    value={form.bankAccountNo}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Confirm Bank Account No.{" "}
                    <span className="fw-bold text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="confirmBankAccountNo"
                    value={form.confirmBankAccountNo}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    IFSC Code <span className="fw-bold text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="ifsc"
                    value={form.ifsc}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Name On Bank Account{" "}
                    <span className="fw-bold text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="nameOnBank"
                    value={form.nameOnBank}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    UPI ID &nbsp;[If Available]
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="upiId"
                    value={form.upiId}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Preview Image */}
            {form.profilePic && (
              <div className="mt-3">
                <img
                  src={form.profilePic}
                  alt="Profile"
                  width="80"
                  height="80"
                  className="rounded-circle border object-fit-cover"
                />
              </div>
            )}

            {/* Message */}
            {message && (
              <div className="mt-3 alert alert-info py-2">{message}</div>
            )}

            {/* Button */}
            <div className="d-flex">
              <button
                className="btn btn-warning text-white rounded-3 px-6 mt-5"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Settings;
